import React, { useRef, useState } from 'react';
import {
    Autocomplete,
    CircularProgress,
    IconButton,
    TextField
} from "@mui/material";

export interface LookupPickerProps<T> {
    label: string;
    required?: boolean;
    isLoading: boolean;
    options: T[];
    isOptionEqualToValue: (option: T, value: T) => boolean;
    getOptionLabel: (option: T) => string;
    value: T | null;
    error?: boolean;
    helperText?: React.ReactNode;
    onInputChange: (event: React.SyntheticEvent<Element, Event>, value: string, reason: string) => void;
    onChange: (event: React.SyntheticEvent, value: T | null) => void;
    onAddClick?: (value: string | undefined) => void;
    disabled?: boolean;
    freeSolo?: boolean;
}

export default function LookupPicker<T>(props: LookupPickerProps<T>): JSX.Element {
    const {
        label,
        required = false,
        isLoading,
        options,
        isOptionEqualToValue,
        getOptionLabel,
        value,
        error,
        helperText,
        onInputChange,
        onChange,
        onAddClick,
        disabled = false,
        freeSolo = false,
    } = props;

    const [ open, setOpen ] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);

    const handleAddClick = () => {
        if (onAddClick !== undefined) {
            onAddClick(inputRef?.current?.value);
        }
    }

    return (
        <Autocomplete
            filterOptions={(x) => x}
            onChange={onChange}
            onInputChange={onInputChange}
            value={value}
            sx={{ width: "95%" }}
            open={open}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            options={options}
            disabled={disabled}
            renderInput={(params) => (
                <TextField
                    {...params}
                    required={required}
                    error={error}
                    helperText={helperText}
                    inputRef={inputRef}
                    variant="standard"
                    label={label}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                                {freeSolo ? <IconButton onClick={handleAddClick}>+</IconButton> : null}
                            </React.Fragment>
                        ),
                    }}
                />
            )}
        />
    )
}
